import React, { ComponentPropsWithRef } from 'react'
import FlagIcon from '@mui/icons-material/SportsScore'
import ChronoIcon from '@mui/icons-material/HourglassTop'
import StepIcon from '@mui/icons-material/AltRoute'
import Typography from '@mui/material/Typography'
import { Chrono } from './Chrono'
import { addMinutesTo } from '../../utils'
import { Stm } from '../../context/types'
import { isArabic, useTranslation } from '../../i18n/useTranslation'
import { useCommonCtx } from '../../context/useCommonCtx'
import { Button, LinearProgress, useTheme } from '@mui/material'
import { Icons } from '../../assets/icons'
import { getFlags } from './useProgress'
import { RoundsProgress } from './RoundsProgress'
import { SingleProgress } from './SingleProgress'
import { FSM_STATES } from '../../context/defaultState'
import { FullParameters, PartialParameters } from '../../parameters/types'
import { info } from '../../services/sentryLogger'

interface Props extends ComponentPropsWithRef<'div'> {
    progress: number
    simple_progress_bar?: boolean
    progresses?: number[]
    currentStms: Stm[]
    currentStmIndex?: number
    startedAt: Date
    remainingSteps?: number
    remainingTime?: number
    currentRound?: number
    steps?: number
    isRunning: boolean
    params: FullParameters
}
export const BottomProgress = (p: Props) => {
    const {
        currentStms,
        currentStmIndex,
        startedAt,
        isRunning,
        params, // FIXME duplicated with commons
        steps,
        currentRound,
        simple_progress_bar = false,
        remainingTime = 0,
        progresses = [0, 0, 0, 0],
        progress,
    } = p

    const commons = useCommonCtx()
    const muiTheme = useTheme()
    const t = useTranslation(commons)
    const { isConfirming, isStateWithoutProfile } = getFlags(
        commons.state.fsmState
    )
    const showData = false

    const is360 = params.assessmentParams.is360
    
    const progressRound = (progresses[0] + progresses[1] + progresses[2]);
    const maxProgressRound = 3 * 100;
    const FACTOR = 2;
    const progressBucket = (progresses[3]/FACTOR);
    const maxProgressBucket = 100/FACTOR;
    const simpleProgress = ((progressRound + progressBucket) / (maxProgressRound + maxProgressBucket)) * 100;


    return (
        <div className="flex flex-col justify-around my-5">
            {is360 && <SingleProgress value={progress} />}
            {!is360 && simple_progress_bar && (
                <LinearProgress
                    variant="determinate"
                    className="h-4"
                    style={{
                        backgroundColor: muiTheme.palette.background.default,
                    }}
                    value={simpleProgress}
                />
            )}
            {!is360 && !simple_progress_bar && (
                <RoundsProgress
                    variant="determinate"
                    round1={progresses[0]}
                    round2={progresses[1]}
                    round3={progresses[2]}
                    finalRound={progresses[3]}
                    sx={{ marginBottom: 2 }}
                />
            )}
            {showData && (
                <div className="flex flex-row justify-around lg:justify-center lg:space-x-8 items-center">
                    <Typography
                        variant="caption"
                        className={`flex items-center justify-center ${
                            isArabic(params) ? 'text-right' : 'text-left'
                        }`}
                    >
                        <ChronoIcon fontSize="small" className="mr-2" />
                        <Chrono isRunning={isRunning} />
                    </Typography>
                    {p.remainingSteps !== undefined &&
                        params.assessmentParams['Remaining steps'] && (
                            <Typography
                                variant="caption"
                                className={`flex items-center justify-center ${
                                    isArabic(params)
                                        ? 'text-right'
                                        : 'text-left'
                                } space-x-2`}
                            >
                                <StepIcon fontSize="small" className="mr-2" />
                                {`${p.remainingSteps}`}
                            </Typography>
                        )}
                    {steps !== undefined && (
                        <Typography
                            variant="caption"
                            className={`flex items-center justify-center ${
                                isArabic(params) ? 'text-right' : 'text-left'
                            }`}
                        >
                            <StepIcon fontSize="small" className="mr-2" />
                            {`${steps}`}
                        </Typography>
                    )}
                    {currentRound !== undefined &&
                        !!params.assessmentParams['debug_mode'] && (
                            <Typography
                                variant="caption"
                                className={`${
                                    isArabic(params)
                                        ? 'text-right'
                                        : 'text-left'
                                }`}
                            >
                                {`${t('round')} ${currentRound}/${
                                    params.assessmentParams['Nb of rounds']
                                }`}
                            </Typography>
                        )}
                    {currentStmIndex !== undefined &&
                        !!params.assessmentParams['debug_mode'] && (
                            <Typography
                                variant="caption"
                                className={`${
                                    isArabic(params)
                                        ? 'text-right'
                                        : 'text-left'
                                }`}
                            >
                                {`${t('stm')} ${currentStmIndex + 1}/${
                                    currentStms.length
                                }`}
                            </Typography>
                        )}
                    {/* <Typography variant="caption" className="${isArabic(params) ? 'text-right' : 'text-left'}">
                    {addMinutesTo(startedAt, 15)} -{' '}
                    {addMinutesTo(startedAt, 30)}
                </Typography> */}
                    <Typography
                        variant="caption"
                        className="flex items-center justify-center ${isArabic(params) ? 'text-right' : 'text-left'}"
                    >
                        <FlagIcon fontSize="small" className="mr-2" />
                        {addMinutesTo(startedAt, remainingTime / 60)} -{' '}
                        {addMinutesTo(startedAt, remainingTime / 60 + 10)}
                    </Typography>
                    {!isStateWithoutProfile && (
                        <Button
                            size={'small'}
                            disabled={isConfirming}
                            variant="contained"
                            onClick={() => {
                                info('Profile button clicked')
                                commons.setState((s: any) => ({
                                    ...s,
                                    fsmState: FSM_STATES['RANK_FOR_PROFILE'],
                                }))
                            }}
                            startIcon={<Icons.MenuIcon />}
                        >
                            {t('my_profile')}
                        </Button>
                    )}
                </div>
            )}
        </div>
    )
}
